import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getWebApp } from '@/lib/telegram';
import { Globe2, Zap, ChevronRight } from 'lucide-react';
import { Skeleton, SkeletonCard, SkeletonGrid, SkeletonLine, SkeletonPage } from '@/components/ui/skeleton';
import { IconChip, SectionLabel } from '@/components/ui/calm';
import { motion } from 'motion/react';
import { queryClient } from '@/lib/query-client';
import { shortDateTime } from '@/lib/date';
import { countryName, regionName } from '@/lib/geo';
import { Flag } from '@/components/ui/flag';
import { useSelectedSubscription } from '@/lib/use-selected-subscription';

type Section = 'term' | 'lifetime';

export const Route = createFileRoute('/locations')({
  component: LocationsPage,
  // `?section=lifetime` — серверы раздела «Навсегда»: у него свой поставщик. Без
  // параметра экран берёт раздел выбранной подписки клиента (так его открывает кнопка
  // «Локации» в боте, переведённая в мини-апп, заявка #181).
  validateSearch: (search: Record<string, unknown>): { section?: Section } =>
    search.section === 'lifetime' || search.section === 'term' ? { section: search.section } : {},
  loaderDeps: ({ search }) => ({ section: (search.section ?? 'term') as Section }),
  // Prefetch данных при навигации (TanStack Router запускает loader до render'а
  // компонента). Если в cache уже есть свежие данные — мгновенно, без сети.
  loader: ({ deps }) =>
    queryClient.ensureQueryData({
      queryKey: ['client-locations', deps.section],
      queryFn: () => api.locations(deps.section),
      staleTime: 5 * 60 * 1000,
    }),
});

function LocationsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const requested = Route.useSearch().section;
  const { sub } = useSelectedSubscription();
  const section: Section = requested ?? (sub?.supplier === 'scvpn' ? 'lifetime' : 'term');
  const { data, isPending } = useQuery({
    queryKey: ['client-locations', section],
    queryFn: () => api.locations(section),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/me' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  if (isPending) return <LocationsSkeleton />;

  const total = data?.total ?? 0;
  const regions = data?.regions ?? {};
  const hasData = total > 0;

  return (
    <div className="p-4 pb-24 space-y-5">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="relative overflow-hidden rounded-[28px] calm-card-hero p-6"
      >
        <div className="absolute -top-24 -right-20 w-56 h-56 rounded-full bg-primary blur-[80px] opacity-[0.18] pointer-events-none" />
        <div className="relative z-10 flex items-start gap-4">
          <IconChip icon={Globe2} tone="blue" size={52} radius={16} />
          <div className="min-w-0 flex-1">
            <SectionLabel className="mb-1">{t('Servers')}</SectionLabel>
            <h1 className="text-[30px] font-extrabold tracking-tight leading-tight tabular text-foreground">
              {total} <span className="text-[#8a8c93] font-medium text-2xl">{t('Locations word', { count: total })}</span>
            </h1>
            <p className="text-xs text-[#8a8c93] mt-1.5">{t('Locations auto pick')}</p>
          </div>
        </div>
      </motion.div>

      {/* Regions */}
      {!hasData ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-[#8a8c93]">
          {t('Locations unavailable')}
        </div>
      ) : (
        Object.entries(regions).map(([region, items], idx) => (
          <motion.section
            key={region}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.04 * idx }}
            className="relative overflow-hidden rounded-2xl calm-card"
          >
            <div className="flex items-center justify-between p-4 pb-3">
              <div className="flex items-center gap-2.5">
                <IconChip icon={Globe2} tone="blue" size={32} radius={10} />
                <h2 className="font-bold tracking-tight text-foreground">{regionName(region, t)}</h2>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/8 text-[#c2c4ca] uppercase tracking-wider">
                {items.length}
              </span>
            </div>
            <div className="px-3 pb-3 grid grid-cols-2 gap-2">
              {items.map((loc) => (
                <div
                  key={loc.flag}
                  className="flex items-center gap-2 min-[360px]:gap-2.5 px-2.5 min-[360px]:px-3 py-2.5 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.04)' }}
                >
                  <span className="text-xl leading-none shrink-0">
                    <Flag flag={loc.flag} code={loc.country_code} />
                  </span>
                  {/* Перенос с дефисом вместо многоточия: на 320px «Великоб…» и «Герман…»
                      не читались, а длинное имя ещё и сплющивало флаг. */}
                  <span className="min-w-0 text-[13px] min-[360px]:text-sm font-semibold leading-tight break-words hyphens-auto text-foreground">
                    {countryName(loc.country_code, loc.country_ru, i18n.language)}
                  </span>
                </div>
              ))}
            </div>
          </motion.section>
        ))
      )}

      {/* CTA */}
      {hasData && (
        <Link
          to="/plans"
          className="block rounded-2xl border border-primary/25 p-5 active:scale-[0.98] transition-transform"
          style={{ background: 'linear-gradient(168deg, rgb(var(--brand-primary-rgb) / 0.16), rgb(var(--brand-primary-rgb) / 0.04))' }}
        >
          <div className="flex items-center gap-3">
            <IconChip icon={Zap} tone="blue" size={40} />
            <div className="min-w-0 flex-1">
              <div className="font-bold text-sm text-foreground">{t('Connect to any')}</div>
              <div className="text-[11px] text-[#8a8c93]">{t('Connect to any desc')}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#8a8c93] shrink-0" />
          </div>
        </Link>
      )}

      {data?.synced_at && (
        <p className="text-center text-[10px] text-[#8a8c93]">
          {t('Updated at', { time: shortDateTime(data.synced_at, i18n.language) })}
        </p>
      )}
    </div>
  );
}

/** Hero со счётчиком стран и карточки регионов с сеткой флагов. */
function LocationsSkeleton() {
  return (
    <SkeletonPage className="space-y-5">
      <SkeletonCard className="rounded-[28px] min-h-0 p-6 flex items-start gap-4">
        <Skeleton className="w-[52px] h-[52px] rounded-[16px] shrink-0" />
        <div className="flex-1 min-w-0 space-y-2.5">
          <SkeletonLine size="xs" className="w-20" />
          <SkeletonLine size="hero" className="w-2/3" />
          <SkeletonLine size="sm" className="w-1/2" />
        </div>
      </SkeletonCard>
      {[0, 1, 2].map((i) => (
        <SkeletonCard key={i} className="rounded-2xl min-h-0 overflow-hidden">
          <div className="flex items-center justify-between p-4 pb-3">
            <div className="flex items-center gap-2.5">
              <Skeleton className="w-8 h-8 rounded-[10px]" />
              <SkeletonLine size="lg" className="w-28" />
            </div>
            <Skeleton className="h-4 w-7 rounded-full" />
          </div>
          <SkeletonGrid
            count={4}
            cols={2}
            variant="flat"
            className="px-3 pb-3 gap-2"
            itemClassName="h-11 rounded-xl"
          />
        </SkeletonCard>
      ))}
    </SkeletonPage>
  );
}
